import type {ReactElement} from 'react';
import {useEffect, useState, useRef} from 'react';
import React from 'react';
import type {FeedbackButtonProps} from './feedback-button.types';
import {setUserCompletedEventSurvey} from './feedback-button.types';
import {shouldShowUserEventFeedbackSurvey} from './feedback-button.types';
import {Button, Size, Type} from '@Components/button';
import styles from './feedback-button.module.scss';
import './feedback-button.scss';

export function FeedbackButton({id, floatingFeedbackButtonProps = undefined, onClick = undefined, isStickingToRight = false}: FeedbackButtonProps): ReactElement<any> | undefined {
  const [shouldShowFeedbackButton, setShouldShowFeedbackButton] = useState<boolean>(false);
  const [isHotjarFormVisible, setIsHotjarFormVisible] = useState<boolean>(false);
  const hotJarHTMLContainerRef = useRef<HTMLDivElement>(null);
  const shouldRenderNothing = !shouldShowFeedbackButton;

  useEffect(() => {
    void shouldShowUserEventFeedbackSurvey().then((shouldShow) => {
      setShouldShowFeedbackButton(shouldShow);
    });
  }, []);

  useEffect(() => {
    if (!hotJarHTMLContainerRef.current) {
      return;
    }

    const TARGET_CLASS_NAME = 'css-1owpeli';

    const onDOMModified = (mutationsList: MutationRecord[], observer: MutationObserver): void => {
      for (const mutation of mutationsList) {
        if (mutation.type !== 'childList') {
          continue;
        }

        let endPanel = hotJarHTMLContainerRef.current?.querySelector(`.${TARGET_CLASS_NAME}`);
        endPanel = endPanel ?? hotJarHTMLContainerRef.current?.querySelector('h2');

        if (!endPanel?.textContent?.toLowerCase()?.includes('thanks for your feedback')) {
          continue;
        }

        setTimeout(() => {
          setShouldShowFeedbackButton(false);
          void setUserCompletedEventSurvey();
        }, 3000);
        observer.disconnect();
        return;
      }
    };


    const observer = new MutationObserver(onDOMModified);
    const config = {childList: true, subtree: true};
    observer.observe(hotJarHTMLContainerRef.current, config);

    return (): void => {
      observer.disconnect();
    };
  }, [id, shouldRenderNothing]);

  const onClickBtn = (): void => {
    setIsHotjarFormVisible(!isHotjarFormVisible);

    if (onClick) {
      onClick();
    }
  };

  const getButton = (): ReactElement<any> => {
    return (
      <>
        <Button
          textClasses={styles.text}
          customClasses={`${styles.buttonContainer} ${id ? styles.hotjarSelector : ''}`}
          type={Type.PRIMARY_DARK}
          size={Size.SMALL}
          icon="icon-chat"
          text={window.i18next.t('pmwjs_tell_us_about_your_experience')}
          onClick={onClickBtn}
        />
        {id ? getHotjarContent() : undefined}
      </>
    );
  };

  const getHotjarContent = (): ReactElement<any> => {
    return (
      <div className={`${styles.hotjarContainer} _overflow-auto ${isHotjarFormVisible ? styles.hotjarVisible : ''}`}>
        <div ref={hotJarHTMLContainerRef} id={id}></div>
      </div>
    );
  };

  const getFloatingButton = (): ReactElement<any> => {
    const style: React.CSSProperties = {
      ...(floatingFeedbackButtonProps?.top !== undefined ? {top: floatingFeedbackButtonProps.top} : {}),
      ...(floatingFeedbackButtonProps?.bottom !== undefined ? {bottom: floatingFeedbackButtonProps.bottom} : {}),
      ...(floatingFeedbackButtonProps?.right !== undefined ? {right: floatingFeedbackButtonProps.right} : {}),
      ...(floatingFeedbackButtonProps?.left !== undefined ? {left: floatingFeedbackButtonProps.left} : {}),
    };

    return (
      <div className={`${styles.floatingContainer} flex-column-align-start`} style={style}>
        {getButton()}
      </div>
    );
  };

  if (shouldRenderNothing) {
    return undefined;
  }

  if (floatingFeedbackButtonProps) {
    return getFloatingButton();
  }

  if (isStickingToRight) {
    return <div className={`${styles.floatingContainer} ${styles.rightStuck} flex-column-align-start`}>{getButton()}</div>;
  }
  return getButton();
}
